<template>
  <div class="comments-item d-flex column-gap-3" :class="{'comments-item--fading': isFading}" data-locator="comment-item">
    <app-avatar class="comment-avatar no-shrink" :initials="item.user.initials" />
    <div class="w-100">
      <div class="d-flex column-gap-2">
        <div class="detail-md-bold text-text-primary" v-html="displayUserFullName"></div>
        <span class="detail-md-regular text-text-placeholder">{{ displayDate }}</span>
      </div>
      <div class="w-100 mt-1 text-text-secondary" v-html="displayMessage"></div>
    </div>
    <AppMenu :menu="menuItems" v-if="menuItems.length" data-locator="checklist-comment-item-menu">
      <template #activator="{ props }">
        <app-button type="icon" data-locator="other-actions-menu-button" v-bind="props">
          <app-material-icon name="more_horiz" />
        </app-button>
      </template>
    </AppMenu>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import AppMenu from '@/common/AppMenu.vue'
import { sanitizeHtml } from '@/common/helpers/htmlHelpers'
import { ISOToShortDate } from '@/common/utils/dates'

const TRANSLATION_KEY_PREFIX = 'project.checklist.panes.ChecklistPaneCommentsItem'

export default {
  name: 'ChecklistPaneCommentsItem',
  components: { AppMenu },
  emits: ['delete-comment'],
  props: {
    isFading: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      required: true,
    },
    searchQuery: {
      type: String,
      default: '',
    },
  },
  methods: {
    deleteComment() {
      this.$emit('delete-comment', this.item.id)
    },
    highlightText(string, substring) {
      if (!substring) return string

      const regexRule = new RegExp(`(${substring})`, 'gi')
      const parser = new DOMParser()
      const doc = parser.parseFromString(string, 'text/html')

      const highlightNode = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const tempDiv = document.createElement('div')

          tempDiv.innerHTML = node.nodeValue.replace(regexRule, '<span class="text--highlight">$1</span>')

          while (tempDiv.firstChild) {
            node.parentNode.insertBefore(tempDiv.firstChild, node)
          }

          node.parentNode.removeChild(node)
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          node.childNodes.forEach(highlightNode)
        }
      };

      doc.body.childNodes.forEach(highlightNode)
      return doc.body.innerHTML
    },
  },
  computed: {
    ...mapState('user', ['profile']),
    canDeleteComment() {
      return this.item.user.userId === this.profile.id
    },
    displayDate() {
      return ISOToShortDate(this.item.createdAt)
    },
    // sanitization must stay after highlightText: it re-parses text nodes as HTML,
    // so escaped markup in the source would come back as live elements
    displayMessage() {
      if(!this.hasSearchQuery) return sanitizeHtml(this.item.message)
      return sanitizeHtml(this.highlightText(this.item.message, this.searchQuery))
    },
    displayUserFullName() {
      if(!this.hasSearchQuery) return sanitizeHtml(this.item.user.fullName)
      return sanitizeHtml(this.highlightText(this.item.user.fullName, this.searchQuery))
    },
    hasSearchQuery() {
      return this.searchQuery.length > 1
    },
    menuItems() {
      const menuItems = []
      if(this.canDeleteComment) {
        menuItems.push({
          icon: 'delete',
          iconColor: 'icon-danger',
          id: 'delete-comment',
          style: 'text-text-danger',
          title: this.$t(`${TRANSLATION_KEY_PREFIX}.menuItems.deleteComment`),
          action: () => this.deleteComment(),
        })
      }

      return menuItems
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/scss/border-tokens";

.comments-item {
  &--fading {
    opacity: 0.5;
  }

  .comment-avatar {
    background-color: rgb(var(--v-theme-grey-lighten-5));
    border: get-border();
    color: rgb(var(--v-theme-grey-600));
  }
}
:deep(.text--highlight) {
  background-color: rgb(var(--v-theme-dark-blue-lighten-4));
}
:deep(span[data-mention]) {
  color: rgb(var(--v-theme-info-darken-1));
  display: inline-block;

  &:before {
    content: '@';
  }
}
</style>
